import * as React from 'react';

/**
 * Mock session provider.
 *
 * This is the single integration point for real authentication. When wiring
 * Microsoft Entra ID (via @azure/msal-react) or another OIDC provider, replace
 * the internals of <UserProvider> so `user` comes from the identity token and
 * `signIn` / `signOut` delegate to the auth library. Consumers (profile menu,
 * admin guard, sidebar) only depend on this contract and need no changes.
 */

export type UserRole = 'admin' | 'member';

export type SessionUser = {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  /** Short initials for the avatar fallback. */
  initials: string;
};

export type Session =
  | { status: 'signedIn'; user: SessionUser }
  | { status: 'signedOut' };

type UserContextValue = {
  session: Session;
  signIn: () => void;
  signOut: () => void;
};

const DEMO_USER: SessionUser = {
  id: 'demo-user',
  name: 'Ada Lovelace',
  email: 'ada.lovelace@example.com',
  role: 'admin',
  initials: 'AL'
};

const UserContext = React.createContext<UserContextValue | undefined>(undefined);

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = React.useState<Session>({
    status: 'signedIn',
    user: DEMO_USER
  });

  const value = React.useMemo<UserContextValue>(
    () => ({
      session,
      signIn: () => setSession({ status: 'signedIn', user: DEMO_USER }),
      signOut: () => setSession({ status: 'signedOut' })
    }),
    [session]
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export function useSession(): UserContextValue {
  const context = React.useContext(UserContext);
  if (!context) {
    throw new Error('useSession must be used within a <UserProvider>');
  }
  return context;
}

/** Convenience hook for components that only render when signed in. */
export function useUser(): SessionUser {
  const { session } = useSession();
  if (session.status !== 'signedIn') {
    throw new Error('useUser called while signed out — gate with <UserProvider> session state');
  }
  return session.user;
}
